import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useTranslation } from "react-i18next";
import { Swords, AlertTriangle, Flame, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";

export function ChallengePanel({ challenge, onRun, loading }) {
  const { t } = useTranslation();
  return (
    <div
      className="card-crisp overflow-hidden border-warning/40"
      data-testid="challenge-panel"
    >
      <div className="flex items-center justify-between border-b border-border bg-warning/5 p-5">
        <div className="flex items-center gap-2.5">
          <div className="flex h-8 w-8 items-center justify-center rounded-md bg-warning/15">
            <Swords className="h-4 w-4 text-warning" />
          </div>
          <div>
            <h3 className="font-display text-base font-bold">Challenge My Decision</h3>
            <p className="text-xs text-muted-foreground">
              An adversarial agent attacks the recommendation
            </p>
          </div>
        </div>
        {!challenge && (
          <Button
            onClick={onRun}
            disabled={loading}
            variant="outline"
            className="border-warning/50 text-warning hover:bg-warning/10"
            data-testid="run-challenge-btn"
          >
            {loading ? t("challenge.running") : t("challenge.run")}
          </Button>
        )}
      </div>

      {challenge && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="space-y-5 p-6"
          data-testid="challenge-result"
        >
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Severity</span>
            <Badge
              className={
                challenge.severity === "high"
                  ? "bg-destructive text-white"
                  : challenge.severity === "low"
                  ? "bg-success text-white"
                  : "bg-warning text-white"
              }
              data-testid="challenge-severity"
            >
              {challenge.severity}
            </Badge>
          </div>

          {challenge.verdict && (
            <p className="rounded-md border-l-2 border-warning bg-warning/5 py-2 pl-3 text-sm italic">
              {challenge.verdict}
            </p>
          )}

          <Section icon={AlertTriangle} title={t("challenge.weaknesses")} items={challenge.weaknesses} />
          <Section icon={Flame} title={t("challenge.risks")} items={challenge.risks} />
          <Section
            icon={ArrowRight}
            title={t("challenge.whenWins")}
            items={challenge.when_another_option_wins}
          />
          {challenge.overlooked_factors?.length > 0 && (
            <Section
              icon={AlertTriangle}
              title={t("challenge.overlooked")}
              items={challenge.overlooked_factors}
            />
          )}
        </motion.div>
      )}
    </div>
  );
}

function Section({ icon: Icon, title, items }) {
  if (!items?.length) return null;
  return (
    <div>
      <h4 className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-overline text-muted-foreground">
        <Icon className="h-3.5 w-3.5" /> {title}
      </h4>
      <ul className="space-y-2">
        {items.map((it, i) => (
          <li key={`${title}-${i}`} className="text-sm leading-relaxed text-foreground">
            {it}
          </li>
        ))}
      </ul>
    </div>
  );
}
